import React from 'react';

import { Button } from '@/components/shared/buttons/button';
import { Icon } from '@/components/shared/element/icons';
import { Text } from '@/components/shared/element/text';
import { Box } from '@/components/shared/layout/box';
import { formatPrice } from '@/components/shared/utility/format';
import { CSS, styled } from '@/stitches.config';
import { IMy24LineupItem, IMy24Theme, IMy24Tracking } from '@/types/shared';

const ContentText = styled(Box);

const StyledContent = styled(Box, {
  variants: {
    type: {
      standard: {
        flexDirection: 'column',
      },
      'one-row': {
        flexDirection: 'column',
        '@lg': {
          flexDirection: 'row',
        },
        [`& ${ContentText}`]: {
          '@lg': {
            flex: '0 0 288px',
            maxWidth: 288,
            textAlign: 'left',
          },
        },
      },
      'with-icon': {
        width: '100%',
        flexDirection: 'row',
      },
    },
  },
});

interface IProps {
  css?: CSS;
  className?: string;
  theme?: IMy24Theme;
  size?: 'sm' | 'base';
  type?: 'standard' | 'one-row' | 'with-icon';
  item?: IMy24LineupItem;
  dataTest?: string;
  dataTrack?: IMy24Tracking;
}

export const ContentWithAction2 = React.memo(
  ({
    css,
    className,
    theme = 'light',
    size = 'base',
    type = 'standard',
    item,
    dataTest = 'lineup',
    dataTrack,
  }: IProps) => {
    const textVariant = theme === 'dark' ? 'text-white' : 'text-gray-darkest';
    const buttonProps = item?.button || item?.buttons?.[0];
    const buttonExists = !!buttonProps;

    return (
      <StyledContent
        display="flex"
        flexWrap="wrap"
        alignItems="center"
        justifyContent={type === 'with-icon' ? 'between' : 'center'}
        gap="6"
        textAlign={type === 'with-icon' ? 'left' : 'center'}
        css={css}
        className={className}
        type={type}
      >
        <ContentText className="content-text">
          {!!item?.title && (
            <Text
              as="h3"
              variant={textVariant}
              size={size === 'sm' ? 'text-h3' : 'h2'}
              data-test={`${dataTest}_item_title`}
            >
              {item?.title}
            </Text>
          )}
          {!!item?.price && (
            <Text
              as="p"
              variant={textVariant}
              size={size === 'sm' ? 'text-body-2' : 'text-body-1'}
              data-test={`${dataTest}_item_price`}
              css={size === 'base' ? { marginTop: '$space-1' } : undefined}
            >
              เริ่มต้น {formatPrice(item?.price)}
            </Text>
          )}
          {!item?.price && !!item?.preTitle && (
            <Text
              as="p"
              variant={textVariant}
              size="text-body-1"
              wrap
              dangerouslySetInnerHTML={{
                __html: item?.preTitle,
              }}
              data-test={`${dataTest}_item_preTitle`}
              css={size === 'base' ? { marginTop: '$space-1' } : undefined}
            />
          )}
        </ContentText>
        {!!buttonExists && (
          <>
            {(type === 'standard' || type === 'one-row') && (
              <Button
                className="content-button"
                href={buttonProps.url}
                target={buttonProps.target}
                border={buttonProps.border}
                label={buttonProps.label}
                icon={buttonProps.icon}
                variant={buttonProps.color}
                size={buttonProps.size}
                isMinWidth={type === 'standard' && buttonProps.isMinWidth}
                tracking={{
                  dataTrack: dataTrack as string,
                  dataTrackSection: dataTest,
                  dataTrackText: buttonProps.label,
                  dataTrackValue: item?.title,
                  dataTrackUrl: buttonProps.url,
                }}
                test={{
                  dataTest: `${dataTest}_item_ctaButton`,
                }}
              />
            )}
            {type === 'with-icon' && (
              <Icon
                icon={buttonProps.icon}
                color={theme === 'dark' ? 'icon-white' : 'icon-gray'}
                size={{ '@initial': 'lg', '@xl': '2xl' }}
                data-test={`${dataTest}_item_ctaButton`}
              />
            )}
          </>
        )}
      </StyledContent>
    );
  }
);

ContentWithAction2.displayName = 'ContentWithAction';
